import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import router from "../../router";
import { ERequestType, fetchGetHelper } from "../../tools/fetch";
import { dateToTimeStamp } from "../../tools/timeTool";
dayjs.extend(weekOfYear);

const monthLabels = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const today = new Date();

const state = {
	initialized: false,
	currentStep: 0,
	selectedService: null,
	nbCouvert: 0,
	availableDays: null,
	availablePlaces: null,
	availableTabs: null,
	month: today.getMonth(),
	year: today.getFullYear(),
	day: today.getDate(),
	eventInfos: null,
	isLoading: {
		availableDays: false,
		availablePlaces: false,
		availableTabs: false,
	},
};

const getters = {
	getInitialized: (state) => {
		return state.initialized;
	},
	getCurrentStep: (state) => {
		return state.currentStep;
	},
	getSelectedService: (state) => {
		return state.selectedService;
	},
	getAvailableDays: (state) => {
		return state.availableDays;
	},
	getAvailablePlaces: (state) => {
		return state.availablePlaces;
	},
	getAvailableTabs: (state) => {
		return state.availableTabs;
	},
	getEventInfos: (state) => {
		return state.eventInfos;
	},
	getSelectedDateTs: (state) => {
		return dateToTimeStamp(state.day, state.month - 1, state.year);
	},
	getSelectedDateObject: (state) => {
		const date = dayjs()
			.month(state.month - 1)
			.year(state.year)
			.date(state.day)
			.hour(0)
			.minute(0)
			.second(0)
			.millisecond(0);
		const labelDay = date.format("dddd");
		const labelMonth = date.format("MMMM");
		return {
			label: date.date().toString(),
			day: date.date(),
			weekday: date.day() + 1,
			week: date.week(),
			month: date.month() + 1,
			year: date.year(),
			date: date.toISOString(),
			beforeMonth: false,
			afterMonth: false,
			inMonth: true,
			labelDay: labelDay.charAt(0).toUpperCase() + labelDay.slice(1),
			labelMonth: labelMonth.charAt(0).toUpperCase() + labelMonth.slice(1),
			isToday: date.isSame(dayjs(), "day"),
			isSelected: true,
			isFirstDay: date.date() === 1,
			isLastDay: date.date() === date.daysInMonth(),
		};
	},
	getCurrentDateShorter: (state) => {
		if (state.month === 0) return;
		const month = monthLabels[state.month - 1];
		return `${state.day} ${month.slice(0, 3)}.`;
	},
	getNbCouvert: (state) => {
		return state.nbCouvert;
	},
	isLoadingDays: (state) => {
		return state.isLoading.availableDays;
	},
	isLoadingPlaces: (state) => {
		return state.isLoading.availablePlaces;
	},
	isLoadingTabs: (state) => {
		return state.isLoading.availableTabs;
	},
	getPreselectedSteps: (state) => {},
};

const mutations = {
	setInitialized(state, isInitialized) {
		state.initialized = isInitialized;
	},
	setAvailableDays(state, dbData) {
		state.availableDays = dbData;
	},
	setEventInfos(state, dbData) {
		state.eventInfos = dbData;
	},
	setAvailablePlaces(state, dbData) {
		state.availablePlaces = dbData;
	},
	setAvailableTabs(state, dbData) {
		state.availableTabs = dbData;
	},
	setLoadingDays(state, isLoading) {
		state.isLoading.availableDays = isLoading;
	},
	setLoadingPlaces(state, isLoading) {
		state.isLoading.availablePlaces = isLoading;
	},
	setLoadingTabs(state, isLoading) {
		state.isLoading.availableTabs = isLoading;
	},
	setMonth(state, month) {
		state.month = month;
	},
	setYear(state, year) {
		state.year = year;
	},
	setDay(state, day) {
		state.day = day;
	},
	setNbCouvert(state, nbCouvert) {
		state.nbCouvert = nbCouvert;
	},
	setSelectedService(state, selectedService) {
		state.selectedService = selectedService;
	},
};

const actions = {
	setInitialized({ commit }, isInitialized) {
		commit("setInitialized", isInitialized);
	},
	resetForkBooking: function ({ commit, dispatch }) {
		commit("setAvailableDays", null);
		commit("setAvailablePlaces", null);
		commit("setAvailableTabs", null);
		commit("setNbCouvert", 0);
		commit("setSelectedService", null);
		dispatch("resetDate");
		state.currentStep = 0;
	},
	setCurrentStep: function ({ commit, dispatch }, payload) {
		if (payload === 0) {
			dispatch("resetDate");
		}
		if (payload < 2) {
			commit("setNbCouvert", 0);
		}
		if (payload < 3) {
			commit("setSelectedService", null);
		}
		state.currentStep = payload;
	},
	resetDate: function ({ commit }) {
		commit("setMonth", today.getMonth());
		commit("setYear", today.getFullYear());
		commit("setDay", today.getDate());
	},
	setSelectedService: function ({ commit }, payload) {
		commit("setSelectedService", payload);
	},
	setNbCouvert: function ({ state, dispatch, commit }, payload) {
		state.nbCouvert = payload;
		commit("setAvailableTabs", []);
		dispatch("loadAvailableTabs", payload);
	},
	setSelectedDayTo: function ({ state, dispatch, commit }, payload) {
		if (payload.month !== state.month) {
			state.month = payload.month;
		}
		if (payload.year !== state.year) {
			state.year = payload.year;
		}
		if (payload.day !== state.day) {
			state.day = payload.day;
		}
		commit("setAvailablePlaces", []);
		dispatch("loadAvailablePlaces", payload);
	},
	loadAvailableDays: function ({ commit }) {
		const uid = router.currentRoute.params.id;
		const eventid = router.currentRoute.params.eventid;

		commit("setLoadingDays", true);
		return new Promise((resolve, reject) => {
			fetchGetHelper(ERequestType.FORKBOOKING.GET_AVAILABLE_DAYS, `restoid=${uid || ""}${eventid ? `&eventid=${eventid}` : ""}`)
				.then((res) => {
					commit("setAvailableDays", res.data.dates);
					if (res.data.eventInfos) commit("setEventInfos", res.data.eventInfos);
					commit("setLoadingDays", false);
					resolve();
				})
				.catch((err) => {
					console.error("Error on loading availableDays", err);
					commit("setLoadingDays", false);
					reject(err);
				});
		});
	},
	loadAvailablePlaces: function ({ commit }, payload) {
		const month = payload.month;
		const year = payload.year;
		const day = payload.day;

		let uid = router.currentRoute.params.id;
		const eventid = router.currentRoute.params.eventid;

		commit("setLoadingPlaces", true);
		return new Promise((resolve, reject) => {
			fetchGetHelper(
				ERequestType.FORKBOOKING.GET_AVAILABLE_PLACES,
				`restoid=${uid || ""}&month=${month}&year=${year}&day=${day}${eventid ? `&eventid=${eventid}` : ""}`,
			)
				.then((res) => {
					commit("setAvailablePlaces", res.data.places);
					commit("setLoadingPlaces", false);
					resolve();
				})
				.catch((err) => {
					console.error("Error on loading availablePlaces", err);
					commit("setLoadingPlaces", false);
					reject(err);
				});
		});
	},
	loadAvailableTabs: function ({ commit }, payload) {
		let uid = router.currentRoute.params.id;
		const eventid = router.currentRoute.params.eventid;

		commit("setLoadingTabs", true);
		return new Promise((resolve, reject) => {
			fetchGetHelper(
				ERequestType.FORKBOOKING.GET_AVAILABLE_TABS,
				`restoid=${uid || ""}&month=${state.month}&year=${state.year}&day=${state.day}&nbPersons=${payload}${eventid ? `&eventid=${eventid}` : ""}`,
			)
				.then((res) => {
					commit("setAvailableTabs", res.data.services);
					commit("setLoadingTabs", false);
					resolve();
				})
				.catch((err) => {
					console.error("Error on loading availableTabs", err);
					commit("setLoadingTabs", false);
					reject(err);
				});
		});
	},
	loadPreselectedSteps: function ({ state, commit, dispatch }, payload) {
		let uid = router.currentRoute.params.id;

		commit("setLoadingDays", true);
		return new Promise((resolve, reject) => {
			fetchGetHelper(ERequestType.FORKBOOKING.GET_PRESELECTED_STEPS, `restoid=${uid || ""}&start=${payload.start}&nbPersons=${payload.nbPersons}`)
				.then((res) => {
					const step = res.data.step;
					const day = res.data.day;
					const month = res.data.month;
					const year = res.data.year;
					const nbPersons = res.data.nbPersons;

					commit("setAvailableDays", res.data.days.dates);
					if (step > 0) {
						state.month = month;
						state.year = year;
						state.day = day;
						commit("setAvailablePlaces", res.data.places.places);
					}
					if (step > 1) {
						state.nbCouvert = nbPersons;
						commit("setAvailableTabs", res.data.tabs.services);
					}
					if (step > 2 && res.data.selectedService) {
						commit("setSelectedService", res.data.selectedService);
					}
					dispatch("setCurrentStep", step);
					commit("setLoadingDays", false);
					resolve();
				})
				.catch((err) => {
					console.error("Error on loading availableTabs", err);
					commit("setLoadingDays", false);
					reject(err);
				});
		});
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
